import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RotateCcw, Eye, Pencil, CheckCircle2, User, Briefcase, Mail, Phone, Calendar, Languages, Scale, UserCheck, SlidersHorizontal } from 'lucide-react';
import { allCountries, civilities } from '../../data/quoteFormOptions';
import { getRegionOptions, getVilleOptions } from '../../data/geoData';
import {
  WORK_LANGUAGES,
  LANGUAGE_CODE,
  INTERVENTION,
  INTERVENTION_LABELS,
  LAWYER_SPECIALTIES,
  NOTARY_SPECIALTIES,
  LAWYER_TYPES,
  NOTARY_TYPES,
} from './AdminAddVerifierPage';
import { adminApi } from '../../api/client';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import DashModal from '../../components/dashboard/DashModal';
import AdminAddVerifierPage from './AdminAddVerifierPage';
import './AdminHome.css';
import './AdminOrders.css';
import '../../components/dashboard/AdminTranslatorFilters.css';

const ROLE_LABELS = { lawyer: 'Avocat', notary: 'Notaire' };

const AVAILABILITY_LABELS = {
  available: 'Disponible',
  busy: 'Partiellement disponible',
  unavailable: 'Indisponible',
};

const STATUS_LABELS = {
  active: 'Actif',
  inactive: 'Inactif',
};

const AVAILABILITY_CLASS = {
  available: 'active',
  busy: 'busy',
  unavailable: 'unavailable',
};

function getStatus(v) {
  return v.isActive === false ? 'inactive' : 'active';
}

export default function AdminVerifiersPage() {
  const navigate = useNavigate();
  const [verifiers, setVerifiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [availFilter, setAvailFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadVerifiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVerifiers = () => {
    setLoading(true);
    adminApi
      .verifiers()
      .then((res) => setVerifiers(res.verifiers || []))
      .catch(() => setError('Erreur de chargement des vérificateurs'))
      .finally(() => setLoading(false));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return verifiers.filter((v) => {
      if (typeFilter !== 'all' && v.role !== typeFilter) return false;
      if (q) {
        const text = [
          v.name,
          v.firstName,
          v.lastName,
          v.email,
          v.professionalEmail,
          v.firm,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!text.includes(q)) return false;
      }
      const avail = v.verifierProfile?.availability || v.availability;
      if (availFilter !== 'all' && avail !== availFilter) return false;
      if (statusFilter !== 'all' && getStatus(v) !== statusFilter) return false;
      return true;
    });
  }, [verifiers, typeFilter, search, availFilter, statusFilter]);

  const pg = usePagination(filtered, 6);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleToggleClick = (v) => setConfirmToggle(v);

  const handleConfirmToggle = async () => {
    if (!confirmToggle) return;
    const nextActive = !confirmToggle.isActive;
    const availability = nextActive ? 'available' : 'unavailable';
    setToggleBusy(true);
    try {
      await adminApi.updateVerifierStatus(confirmToggle.id, { isActive: nextActive, availability });
      setVerifiers((prev) =>
        prev.map((x) =>
          x.id === confirmToggle.id
            ? {
                ...x,
                isActive: nextActive,
                availability,
                verifierProfile: { ...x.verifierProfile, availability },
              }
            : x
        )
      );
      setConfirmToggle(null);
      showToast(nextActive ? 'Vérificateur activé' : 'Vérificateur désactivé');
    } catch {
      setError('Erreur lors du changement de statut');
    } finally {
      setToggleBusy(false);
    }
  };

  const handleEditSave = async (form) => {
    setSaving(true);
    try {
      const res = await adminApi.updateVerifier(editing.id, {
        name: `${form.firstName} ${form.lastName}`.trim() || form.name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        region: form.region,
        country: form.country,
        role: form.role,
        isActive: form.isActive,
        verifierProfile: {
          civility: form.civility,
          firstName: form.firstName,
          lastName: form.lastName,
          professionalEmail: form.professionalEmail,
          phone: form.professionalPhone,
          firm: form.firm,
          professionalNumber: form.professionalNumber,
          professionalBody: form.professionalBody,
          availability: form.availability,
          city: form.city,
          region: form.region,
          country: form.country,
          experienceYears: form.experienceYears,
          interventionMode: form.interventionMode,
          languages: form.languages,
          specialties: form.specialty ? [form.specialty] : [],
          verificationTypes: form.verificationType ? [form.verificationType] : [],
          type: form.role,
        },
      });
      setVerifiers((prev) => prev.map((x) => (x.id === editing.id ? res.verifier : x)));
      setEditing(null);
      showToast('Modifications enregistrées');
    } catch {
      setError('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setAvailFilter('all');
    setStatusFilter('all');
  };

  if (loading) return <p className="dash-empty">Chargement…</p>;

  const statTabs = [
    { id: 'all', icon: User, label: 'Tous', value: verifiers.length },
    { id: 'lawyer', icon: Scale, label: 'Avocats', value: verifiers.filter((v) => v.role === 'lawyer').length },
    { id: 'notary', icon: Briefcase, label: 'Notaires', value: verifiers.filter((v) => v.role === 'notary').length },
    { id: 'active', icon: UserCheck, label: 'Actifs', value: verifiers.filter((v) => v.isActive !== false).length },
  ];
  const activeStat = statusFilter === 'active' ? 'active'
    : typeFilter === 'lawyer' ? 'lawyer'
    : typeFilter === 'notary' ? 'notary'
    : 'all';
  const applyStat = (id) => {
    setSearch('');
    setTypeFilter(id === 'lawyer' || id === 'notary' ? id : 'all');
    setStatusFilter(id === 'active' ? 'active' : 'all');
    setAvailFilter('all');
  };
  const initials = (name) =>
    String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();
  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Scale size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>Vérificateurs</h1>
            <p>Gestion des avocats et notaires habilités à la vérification.</p>
          </div>
          <button type="button" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }} onClick={() => setAdding(true)}>
            <Plus size={16} /> Ajouter un vérificateur
          </button>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {statTabs.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={activeStat === s.id}
              className={`orders-stat${activeStat === s.id ? ' is-active' : ''}`}
              onClick={() => applyStat(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>

      {toast && (
        <div className="admin-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{toast}</span>
        </div>
      )}

      {error && <p className="dash-error" role="alert">{error}</p>}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <span>{filtered.length} / {verifiers.length}</span>
            <button type="button" className="of-reset" onClick={clearFilters}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche : nom / prénom, email…"
            />
          </div>
          <div className="of-field">
            <label>Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">Tous les types</option>
              <option value="lawyer">Avocat</option>
              <option value="notary">Notaire</option>
            </select>
          </div>
          <div className="of-field">
            <label>Disponibilité</label>
            <select value={availFilter} onChange={(e) => setAvailFilter(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="available">Disponible</option>
              <option value="busy">Partiellement disponible</option>
              <option value="unavailable">Indisponible</option>
            </select>
          </div>
          <div className="of-field">
            <label>Statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>Vérificateurs</h2>
        <span className="orders-panel-count">{filtered.length}</span>
      </div>

      {filtered.length === 0 ? (
        <p className="dash-empty">Aucun vérificateur.</p>
      ) : (
        <div className="orders-grid">
          {pg.pageItems.map((v) => {
            const status = getStatus(v);
            const avail = v.verifierProfile?.availability || v.availability;
            const specs = (v.specialties || []).slice(0, 3);
            const types = (v.verificationTypes || []).slice(0, 3);
            return (
              <article key={v.id} className="o-card">
                <div className="o-card-head">
                  <span className="o-client">
                    <span className="o-avatar" aria-hidden="true">{initials(v.name)}</span>
                    <span className="o-card-client">
                      {v.name || '—'}
                      <small className="o-card-sub">{ROLE_LABELS[v.role] || v.role}</small>
                    </span>
                  </span>
                  <span className={`dash-badge dash-badge--${status === 'active' ? 'active' : 'cancelled'}`}>
                    {STATUS_LABELS[status]}
                  </span>
                </div>
                <ul className="o-card-rows">
                  <li><Mail size={14} /><span>{v.email || v.professionalEmail || '—'}</span></li>
                  <li><Phone size={14} /><span>{v.phone || '—'}</span></li>
                  <li>
                    <Languages size={14} />
                    <span>{v.languages ? v.languages.replace(/\s*\(C2\)/g, '') : '—'}</span>
                  </li>
                </ul>
                <div className="o-card-sep" />
                <ul className="o-card-rows">
                  <li>
                    <Briefcase size={14} />
                    <span>{specs.length ? specs.join(', ') : '—'}</span>
                  </li>
                  <li>
                    <Calendar size={14} />
                    <span className="o-date">
                      {types.length ? types.join(', ') : 'Inscrit le ' + fmtDate(v.createdAt)}
                    </span>
                  </li>
                  <li>
                    <span className={`dash-badge dash-badge--${AVAILABILITY_CLASS[avail] || 'pending'}`}>
                      {AVAILABILITY_LABELS[avail] || avail || '—'}
                    </span>
                  </li>
                </ul>
                <div className="o-card-foot">
                  <button type="button" className="o-card-view" onClick={() => navigate(`/admin/verifiers/${v.id}`)}>
                    <Eye size={14} /> Profil
                  </button>
                  <button type="button" className="o-card-view" onClick={() => setEditing(v)}>
                    <Pencil size={14} /> Modifier
                  </button>
                  <button
                    type="button"
                    className={`o-card-view ${v.isActive ? 'o-card-view--danger' : 'o-card-view--success'}`}
                    onClick={() => handleToggleClick(v)}
                  >
                    {v.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />

      <EditVerifierModal
        verifier={editing}
        onClose={() => setEditing(null)}
        onSave={handleEditSave}
        saving={saving}
      />

      <DashModal
        open={Boolean(confirmToggle)}
        title={confirmToggle?.isActive ? 'Désactiver le compte' : 'Activer le compte'}
        subtitle={confirmToggle?.name}
        onClose={() => setConfirmToggle(null)}
      >
        <div style={{ padding: '4px 2px' }}>
          <p style={{ margin: '0 0 18px', fontSize: '0.92rem', lineHeight: 1.6 }}>
            {confirmToggle?.isActive
              ? `Voulez-vous désactiver le compte de ${confirmToggle?.name} ? Il ne pourra plus être affecté à de nouvelles vérifications.`
              : `Voulez-vous activer le compte de ${confirmToggle?.name} ? Il pourra à nouveau être affecté.`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-outline" onClick={() => setConfirmToggle(null)} disabled={toggleBusy}>
              Annuler
            </button>
            <button
              type="button"
              className={`btn ${confirmToggle?.isActive ? 'btn-danger' : 'btn-primary'}`}
              onClick={handleConfirmToggle}
              disabled={toggleBusy}
            >
              {toggleBusy ? 'En cours…' : (confirmToggle?.isActive ? 'Désactiver' : 'Activer')}
            </button>
          </div>
        </div>
      </DashModal>

      <DashModal
        open={Boolean(adding)}
        title="Ajouter un vérificateur"
        onClose={() => setAdding(false)}
        wide
      >
        <AdminAddVerifierPage
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            loadVerifiers();
            showToast('Vérificateur ajouté');
          }}
        />
      </DashModal>
    </>
  );
}

function EditVerifierModal({ verifier, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    civility: '',
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    city: '',
    region: '',
    country: '',
    role: 'lawyer',
    isActive: true,
    professionalEmail: '',
    professionalPhone: '',
    firm: '',
    professionalNumber: '',
    professionalBody: '',
    availability: 'available',
    experienceYears: '',
    interventionMode: 'both',
    languages: [],
    specialty: '',
    verificationType: '',
  });

  useEffect(() => {
    if (!verifier) return;
    const p = verifier.verifierProfile || {};
    setForm({
      name: verifier.name || '',
      civility: p.civility || verifier.civility || '',
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      email: verifier.email || '',
      phone: verifier.phone || '',
      city: verifier.city || p.city || '',
      region: verifier.region || p.region || '',
      country: verifier.country || p.country || '',
      role: verifier.role || 'lawyer',
      isActive: verifier.isActive !== false,
      professionalEmail: p.professionalEmail || '',
      professionalPhone: p.phone || '',
      firm: p.firm || '',
      professionalNumber: p.professionalNumber || '',
      professionalBody: p.professionalBody || '',
      availability: p.availability === 'suspended' ? 'unavailable' : p.availability || 'available',
      experienceYears: p.experienceYears || '',
      interventionMode: p.interventionMode || 'both',
      languages: p.languages || [],
      specialty: (p.specialties || [])[0] || '',
      verificationType: (p.verificationTypes || [])[0] || '',
    });
  }, [verifier]);

  // Cascade Pays → Ville → Région
  const villeOptions = useMemo(() => getVilleOptions(form.country), [form.country]);
  const regionOptions = useMemo(
    () => getRegionOptions(form.country, form.city),
    [form.country, form.city]
  );
  const regionRequired = regionOptions.length > 0;

  if (!verifier) return null;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleLanguage = (opt) =>
    setForm((prev) => {
      const code = LANGUAGE_CODE[opt];
      const exists = prev.languages.some((l) => l.language === code);
      const next = exists
        ? prev.languages.filter((l) => l.language !== code)
        : [...prev.languages, { language: code, level: 'C2' }];
      return { ...prev, languages: next };
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <DashModal open title="Modifier un vérificateur" onClose={onClose} wide>
      <form className="dform" onSubmit={handleSubmit}>
        <section className="dform-section">
          <header className="dform-section-head">
            <User size={17} />
            <h4>Informations communes</h4>
          </header>
          <div className="dform-grid dform-grid--2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="dform-field">
              <label>Civilité <span className="dform-req">*</span></label>
              <select value={form.civility} required onChange={(e) => update('civility', e.target.value)}>
                <option value="">Sélectionner</option>
                {civilities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="dform-field">
              <label>Prénom <span className="dform-req">*</span></label>
              <input value={form.firstName} onChange={(e) => update('firstName', e.target.value)} required />
            </div>
            <div className="dform-field">
              <label>Nom <span className="dform-req">*</span></label>
              <input value={form.lastName} onChange={(e) => update('lastName', e.target.value)} required />
            </div>
            <div className="dform-field">
              <label>Email de connexion <span className="dform-req">*</span></label>
              <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
            </div>
            <div className="dform-field">
              <label>Téléphone</label>
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)} />
            </div>
            <div className="dform-field">
              <label>Email professionnel</label>
              <input type="email" value={form.professionalEmail} onChange={(e) => update('professionalEmail', e.target.value)} />
            </div>
            <div className="dform-field">
              <label>Type de professionnel <span className="dform-req">*</span></label>
              <div className="dform-radio-group" style={{ gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', maxWidth: 280, margin: '8px auto 0' }}>
                {[
                  { value: 'lawyer', label: 'Avocat' },
                  { value: 'notary', label: 'Notaire' },
                ].map((opt) => (
                  <label key={opt.value} className="dform-radio">
                    <input
                      type="radio"
                      name="role"
                      value={opt.value}
                      checked={form.role === opt.value}
                      onChange={(e) => update('role', e.target.value)}
                      required
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="dform-field">
              <label>Pays <span className="dform-req">*</span></label>
              <select
                required
                value={form.country}
                onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value, city: '', region: '' }))}
              >
                <option value="">Sélectionnez un pays</option>
                {allCountries.map(({ code, name }) => (
                  <option key={code} value={code}>{name}</option>
                ))}
                {form.country && !allCountries.some((c) => c.code === form.country) && (
                  <option value={form.country}>{form.country}</option>
                )}
              </select>
            </div>
            <div className="dform-field">
              <label>Ville <span className="dform-req">*</span></label>
              {villeOptions.length ? (
                <select
                  required
                  value={form.city}
                  onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value, region: '' }))}
                >
                  <option value="">Sélectionnez une ville</option>
                  {villeOptions.map((v) => (
                    <option key={v.stateCode || v.name} value={v.name}>{v.name}</option>
                  ))}
                  {form.city && !villeOptions.some((v) => v.name === form.city) && (
                    <option value={form.city}>{form.city}</option>
                  )}
                </select>
              ) : (
                <input required value={form.city} onChange={(e) => update('city', e.target.value)} />
              )}
            </div>
            <div className="dform-field">
              <label>Région {regionRequired && <span className="dform-req">*</span>}</label>
              {regionRequired ? (
                <select required value={form.region} onChange={(e) => update('region', e.target.value)}>
                  <option value="">Sélectionnez une région</option>
                  {regionOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                  {form.region && !regionOptions.includes(form.region) && (
                    <option value={form.region}>{form.region}</option>
                  )}
                </select>
              ) : (
                <input
                  value={form.region}
                  onChange={(e) => update('region', e.target.value)}
                  placeholder="Ex. La Marsa"
                  disabled={villeOptions.length > 0 && !form.city}
                />
              )}
            </div>
          </div>
        </section>

        <section className="dform-section" style={{ marginTop: 14 }}>
          <header className="dform-section-head">
            <Briefcase size={17} />
            <h4>Informations professionnelles</h4>
          </header>
          <div className="dform-grid dform-grid--2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="dform-field">
              <label>Cabinet / Étude</label>
              <input value={form.firm} onChange={(e) => update('firm', e.target.value)} />
            </div>
            <div className="dform-field">
              <label>Numéro professionnel</label>
              <input value={form.professionalNumber} onChange={(e) => update('professionalNumber', e.target.value)} />
            </div>
            <div className="dform-field">
              <label>Barreau / Organisme</label>
              <input value={form.professionalBody} onChange={(e) => update('professionalBody', e.target.value)} />
            </div>
            <div className="dform-field">
              <label>Années d'expérience <span className="dform-req">*</span></label>
              <input type="number" min={0} required value={form.experienceYears} onChange={(e) => update('experienceYears', e.target.value)} />
            </div>
            <div className="dform-field dform-wide">
              <label>Mode d'intervention <span className="dform-req">*</span></label>
              <div className="dform-radio-group" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {INTERVENTION.map((m) => (
                  <label key={m} className="dform-radio">
                    <input
                      type="radio"
                      name="interventionMode"
                      value={m}
                      checked={form.interventionMode === m}
                      onChange={(e) => update('interventionMode', e.target.value)}
                      required
                    />
                    {INTERVENTION_LABELS[m]}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="dform-section" style={{ marginTop: 14 }}>
          <header className="dform-section-head">
            <CheckCircle2 size={17} />
            <h4>Compétences</h4>
          </header>
          <div className="dform-grid dform-grid--2" style={{ gridTemplateColumns: '1fr' }}>
            <div className="dform-field dform-wide">
              <label>Langue de travail <span className="dform-req">*</span></label>
              <div className="dform-checkbox-scroll" style={{ gridTemplateColumns: '1fr', gap: 0, padding: 0, border: 'none', background: 'transparent' }}>
                {WORK_LANGUAGES.map((opt) => (
                  <label key={opt} className="dform-checkbox" style={{ padding: '2px 0', border: 'none', background: 'transparent', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={form.languages.some((l) => l.language === LANGUAGE_CODE[opt])}
                      onChange={() => toggleLanguage(opt)}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="dform-field dform-wide">
              <label>{form.role === 'lawyer' ? 'Domaines de vérification' : "Domaines d'intervention"} <span className="dform-req">*</span></label>
              <div className="dform-radio-group">
                {(form.role === 'lawyer' ? LAWYER_SPECIALTIES : NOTARY_SPECIALTIES).map((opt) => (
                  <label key={opt} className="dform-radio">
                    <input
                      type="radio"
                      name="specialty"
                      value={opt}
                      checked={form.specialty === opt}
                      onChange={(e) => update('specialty', e.target.value)}
                      required
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="dform-field dform-wide">
              <label>{form.role === 'lawyer' ? 'Types de vérification' : "Types d'intervention"} <span className="dform-req">*</span></label>
              <div className="dform-radio-group">
                {(form.role === 'lawyer' ? LAWYER_TYPES : NOTARY_TYPES).map((opt) => (
                  <label key={opt} className="dform-radio">
                    <input
                      type="radio"
                      name="verificationType"
                      value={opt}
                      checked={form.verificationType === opt}
                      onChange={(e) => update('verificationType', e.target.value)}
                      required
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="dform-section" style={{ marginTop: 14 }}>
          <header className="dform-section-head">
            <Mail size={17} />
            <h4>Compte plateforme</h4>
          </header>
          <div className="dform-grid dform-grid--2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="dform-field">
              <label>Disponibilité <span className="dform-req">*</span></label>
              <select value={form.availability} onChange={(e) => update('availability', e.target.value)} required>
                <option value="available">Disponible</option>
                <option value="busy">Partiellement disponible</option>
                <option value="unavailable">Indisponible</option>
              </select>
            </div>
            <div className="dform-field">
              <label>Statut du compte <span className="dform-req">*</span></label>
              <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => update('isActive', e.target.value === 'active')} required>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>
        </section>

        <div className="dform-footer" style={{ marginTop: 18 }}>
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <div className="dform-spacer" />
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </DashModal>
  );
}
